import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { EuiIcon, EuiFieldSearch } from '@elastic/eui';
import { BoxedResponsiveDashboardStyled } from '../ServerItem2';
import utilsService from '../../../../../servises/utils.service';
import MongoCollectionStatsModal from '../../../../components/modal/MongoCollectionStatsModal';

const InspectorCard = styled(BoxedResponsiveDashboardStyled)`
  margin: 0 10px 18px 10px;
  width: calc(100% - 20px);
  padding: 16px;
`;

const SectionHeader = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding-bottom: 8px;
`;

const InspectorGrid = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  grid-gap: 16px;
  min-height: 450px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const DbListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 500px;
  padding-right: 4px;
`;

const DbItem = styled.div`
  background: ${props => props.active ? 'linear-gradient(90deg, rgba(0, 128, 213, 0.1) 0%, rgba(0, 128, 213, 0.02) 100%)' : 'rgba(255, 255, 255, 0.01)'};
  border: 1px solid ${props => props.active ? 'rgba(0, 128, 213, 0.3)' : 'rgba(255, 255, 255, 0.03)'};
  border-left: 3px solid ${props => props.active ? 'rgb(0, 128, 213)' : 'rgba(255, 255, 255, 0.15)'};
  border-radius: 6px;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex-shrink: 0;

  &:hover {
    background: ${props => props.active ? 'linear-gradient(90deg, rgba(0, 128, 213, 0.15) 0%, rgba(0, 128, 213, 0.03) 100%)' : 'rgba(255, 255, 255, 0.03)'};
    border-color: ${props => props.active ? 'rgba(0, 128, 213, 0.4)' : 'rgba(255, 255, 255, 0.1)'};
    transform: translateX(2px);
  }
`;

const DbName = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.active ? '#fff' : 'rgba(255, 255, 255, 0.85)'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DbStatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.45);
  margin-top: 2px;

  span.size {
    font-family: Consolas, monospace;
    color: rgb(0, 128, 213);
    font-weight: 600;
  }
`;

const ExplorerContainer = styled.div`
  background: rgba(255, 255, 255, 0.008);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ExplorerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.05);
  padding-bottom: 12px;

  .meta-info {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.6);
    span {
      font-weight: 700;
      color: rgb(0, 128, 213);
      background: rgba(0, 128, 213, 0.1);
      padding: 1px 6px;
      border-radius: 4px;
      margin: 0 4px;
      font-family: Consolas, monospace;
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 350px;
  color: rgba(255, 255, 255, 0.35);
  gap: 10px;
  font-size: 13px;
`;

const CollectionsTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 420px;
  padding-right: 4px;
`;

const CollectionRowStyled = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  align-items: center;
  background: rgba(255, 255, 255, 0.015);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 11.5px;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.035);
    border-color: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.15);
  }

  .col-name {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    z-index: 2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 8px;
  }

  .col-docs, .col-indexes {
    color: rgba(255, 255, 255, 0.65);
    font-family: Consolas, monospace;
    z-index: 2;
  }

  .col-size {
    font-weight: 600;
    font-family: Consolas, monospace;
    z-index: 2;
    display: flex;
    flex-direction: column;
    gap: 4px;

    span.val {
      color: #50bc5e;
    }
  }

  .foreground-bar {
    z-index: 1;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    background: rgba(80, 188, 94, 0.06);
    pointer-events: none;
    transition: width 0.3s ease;
  }

  button.view-btn {
    opacity: 0.6;
    transition: opacity 0.2s, transform 0.2s;
    background: none;
    border: none;
    cursor: pointer;
    color: #fff;
    padding: 4px;
    z-index: 2;

    &:hover {
      opacity: 1;
      transform: scale(1.15);
      color: rgb(0, 128, 213);
    }
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr auto;
    grid-gap: 8px;
    padding: 8px 10px;

    .col-docs, .col-indexes, .col-size-bar {
      display: none;
    }
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  padding: 6px 14px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  margin-bottom: 4px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr auto;
    .hd-docs, .hd-indexes, .hd-size {
      display: none;
    }
  }
`;

export const getSelectedDatabaseName = (dataBases, selectedDbName) => {
    if (!dataBases || dataBases.length === 0) return '';
    if (dataBases.some(db => db.name === selectedDbName)) return selectedDbName;
    return dataBases[0].name;
};

const MongoDatabaseInspector = ({ dataBases, dbsStats }) => {
    const [selectedDbName, setSelectedDbName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showModal, setShowModal] = useState(null);

    useEffect(() => {
        const nextSelectedDbName = getSelectedDatabaseName(dataBases, selectedDbName);
        if (nextSelectedDbName !== selectedDbName) {
            setSelectedDbName(nextSelectedDbName);
            setSearchQuery('');
        }
    }, [dataBases, selectedDbName]);

    if (!dataBases || dataBases.length === 0) return null;

    const activeDb = dataBases.find(db => db.name === selectedDbName);
    const activeDbInfo = dbsStats ? dbsStats[selectedDbName] : null;

    // Process collections stats
    let listOfCollections = [];
    let maxSize = 0;
    if (activeDbInfo && activeDbInfo.collectionsStats) {
        const collectionsStats = activeDbInfo.collectionsStats;
        const keys = Object.keys(collectionsStats);
        listOfCollections = keys.map((key) => {
            const item = collectionsStats[key];
            item.name = key;
            return item;
        });
        listOfCollections.sort((a, b) => b.totalSize - a.totalSize);
        if (listOfCollections.length > 0) {
            maxSize = listOfCollections[0].totalSize || 0;
        }
    }

    // Filter collections by search query
    const filteredCollections = listOfCollections.filter(col => 
        col.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <InspectorCard className="column-body">
            <SectionHeader><EuiIcon type="storage" size="m"/> Database & Collections Inspector</SectionHeader>
            <InspectorGrid>
                {/* Left Pane: Database List */}
                <div>
                    <DbListContainer>
                        {dataBases.map((db, idx) => {
                            const dbInfo = dbsStats ? dbsStats[db.name] : null;
                            const sizeOnDiskFormatted = utilsService.formatBytes(db.sizeOnDisk);
                            const isActive = db.name === selectedDbName;

                            return (
                                <DbItem 
                                    key={db.name + idx} 
                                    active={isActive}
                                    onClick={() => {
                                        setSelectedDbName(db.name);
                                        setSearchQuery('');
                                    }}
                                >
                                    <DbName active={isActive}>
                                        <EuiIcon type="database" size="m" color={isActive ? "primary" : "text"} />
                                        {db.name}
                                    </DbName>
                                    <DbStatsRow>
                                        <span>Disk: <span className="size">{sizeOnDiskFormatted.value} {sizeOnDiskFormatted.unit}</span></span>
                                        {dbInfo && (
                                            <span>Cols: {dbInfo.dbStats.collections}</span>
                                        )}
                                    </DbStatsRow>
                                </DbItem>
                            );
                        })}
                    </DbListContainer>
                </div>

                {/* Right Pane: Collections Detail Explorer */}
                <div>
                    {selectedDbName ? (
                        <ExplorerContainer>
                            <ExplorerHeader>
                                <div className="meta-info">
                                    Database: <span>{selectedDbName}</span>
                                    {activeDbInfo && (
                                        <>
                                            Collections: <span>{activeDbInfo.dbStats.collections}</span>
                                            Objects: <span>{activeDbInfo.dbStats.objects}</span>
                                        </>
                                    )}
                                </div>
                                <div style={{ width: '220px' }}>
                                    <EuiFieldSearch
                                        placeholder="Search collections..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        isClearable={true}
                                        fullWidth={true}
                                        size="s"
                                    />
                                </div>
                            </ExplorerHeader>

                            {filteredCollections.length > 0 ? (
                                <>
                                    <TableHeader>
                                        <span>Collection Name</span>
                                        <span className="hd-docs">Documents</span>
                                        <span className="hd-indexes">Indexes</span>
                                        <span className="hd-size">Total Size</span>
                                        <span>Stats</span>
                                    </TableHeader>
                                    <CollectionsTable>
                                        {filteredCollections.map((col) => {
                                            const sizePercent = col.totalSize / maxSize * 100;
                                            const safeSizePercent = Math.min(100, Math.max(0, Number.isNaN(sizePercent) ? 0 : sizePercent));
                                            const colSizeFormatted = utilsService.formatBytes(col.totalSize);

                                            return (
                                                <CollectionRowStyled 
                                                    key={col.name}
                                                    onClick={() => setShowModal(col.name)}
                                                >
                                                    <div className="foreground-bar" style={{ width: `${safeSizePercent}%` }} />
                                                    <div className="col-name" title={col.name}>{col.name}</div>
                                                    <div className="col-docs">{col.count}</div>
                                                    <div className="col-indexes">{col.nindexes}</div>
                                                    <div className="col-size">
                                                        <span className="val">{colSizeFormatted.value} {colSizeFormatted.unit}</span>
                                                    </div>
                                                    <button 
                                                        className="view-btn" 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setShowModal(col.name);
                                                        }}
                                                        title="Full Stats"
                                                    >
                                                        <EuiIcon type="inspect" size="s" />
                                                    </button>
                                                </CollectionRowStyled>
                                            );
                                        })}
                                    </CollectionsTable>
                                </>
                            ) : (
                                <EmptyState>
                                    <EuiIcon type="alert" size="l" />
                                    <span>No collections found matching "{searchQuery}"</span>
                                </EmptyState>
                            )}
                        </ExplorerContainer>
                    ) : (
                        <EmptyState>
                            <EuiIcon type="database" size="l" />
                            <span>Select a database from the left pane to explore its collections.</span>
                        </EmptyState>
                    )}
                </div>
            </InspectorGrid>

            {showModal && activeDbInfo && activeDbInfo.collectionsStats && (
                <MongoCollectionStatsModal
                    data={{
                        ...activeDbInfo.collectionsStats[showModal],
                        name: showModal
                    }}
                    close={() => setShowModal(null)}
                />
            )}
        </InspectorCard>
    );
};

export default MongoDatabaseInspector;
